import { Fragment, ReactNode } from "react";
import styles from "./AnalyticsHeatmap.module.scss";
import AnalyticsCard from "../card/AnalyticsCard";
import AnalyticsHeader from "../header/AnalyticsHeader";
import AnalyticsState from "../state/AnalyticsState";

const STEP_COUNT = 6;

export interface AnalyticsHeatmapRow {
	label: string;
	/** One per column. `null` is no data, which is not the same as a value of zero. */
	values: (number | null)[];
}

type Props = {
	title?: string;
	subtitle?: string;
	/** The grid is a stand-in rather than a measurement — see AnalyticsHeader. */
	mock?: boolean;
	/** Sits in the heading's right corner, e.g. a switch for which grid is shown. */
	action?: ReactNode;
	columns: string[];
	rows: AnalyticsHeatmapRow[];
	/** The value the darkest step stands for — percentages leave it at 100. */
	max?: number;
	/** Appended to the cell's hover text, e.g. "%". */
	unit?: string;
	/** Both default to false, for a grid built from data that is already in hand. */
	loading?: boolean;
	error?: boolean;
	/** The query's refetch and whether one is already running — see AnalyticsState. */
	onRetry?: () => void;
	retrying?: boolean;
};

/** A value per row-and-column, shaded by how large it is. */
const AnalyticsHeatmap = ({
	title,
	subtitle,
	mock = false,
	action,
	columns,
	rows,
	max = 100,
	unit = "",
	loading = false,
	error = false,
	onRetry,
	retrying,
}: Readonly<Props>) => {
	const stepOf = (value: number) => Math.min(Math.floor((value / max) * STEP_COUNT), STEP_COUNT - 1);

	return (
		<AnalyticsCard>
			{title && (
				<AnalyticsHeader title={title} subtitle={subtitle} mock={mock}>
					{action}
				</AnalyticsHeader>
			)}
			{/* No rows is the empty state: the grid has nothing else it could be built from. */}
			<AnalyticsState loading={loading} error={error} onRetry={onRetry} retrying={retrying} empty={rows.length === 0}>
				<div className={styles.scroller}>
					<div className={styles.grid} style={{ gridTemplateColumns: `max-content repeat(${columns.length}, 1fr)` }}>
						{/* The corner above the row labels is empty, so it is spanned rather than filled. */}
						<span />
						{columns.map(column => (
							<span key={column} className={styles.columnHeader}>
								{column}
							</span>
						))}

						{rows.map(row => (
							<Fragment key={row.label}>
								<span className={styles.rowLabel}>{row.label}</span>
								{row.values.map((value, index) => (
									<span
										key={columns[index]}
										className={`${styles.cell} ${value === null ? styles.empty : styles[`step${stepOf(value)}`]}`}
										title={`${row.label} · ${columns[index]}: ${value === null ? "no data" : `${value}${unit}`}`}
									>
										{value === null ? "–" : value}
									</span>
								))}
							</Fragment>
						))}
					</div>
				</div>
				{/* Single-hue ramps still need the reader to be told which end is which. */}
				<div className={styles.scale}>
					<span>0{unit}</span>
					{Array.from({ length: STEP_COUNT }, (_, step) => (
						<span key={step} className={`${styles.scaleStep} ${styles[`step${step}`]}`} />
					))}
					<span>
						{max}
						{unit}
					</span>
				</div>
			</AnalyticsState>
		</AnalyticsCard>
	);
};

export default AnalyticsHeatmap;
